import 'brace/ext/language_tools';
import 'brace/ext/searchbox';
import 'brace/mode/python';
import 'brace/mode/sql';
import {createStyles, Group} from '@mantine/core';
import React, {useCallback} from 'react';
import AceEditor from 'react-ace';

import BenchlingConfig from 'BenchlingConfig';
import {useStepConfigEditorContext} from 'pipelines/generalized-step-config-editor/StepConfigEditorContext';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';

import PipelineCustomCodeWriterButton from './PipelineCustomCodeWriterButton';
import {PYTHON_STARTER_CODE} from './StarterCode';

export type CodeEditorMode = 'python' | 'sql';

const DEFAULT_VALUE: Record<CodeEditorMode, string> = {
  python: PYTHON_STARTER_CODE,
  sql: '',
};

interface UserDefinedFunctionEditorProps extends CustomComponentEditorProps {
  fieldName: string;
  mode?: CodeEditorMode;
}

const useStyles = createStyles(() => ({
  editorWrapper: {
    width: '100%',
    height: 'calc(100vh - 320px)',
    minHeight: 300,
  },
}));

export default function UserDefinedFunctionEditor({
  config: fullConfig,
  onChangeConfig,
  disabled,
  fieldName,
  mode = 'python',
}: UserDefinedFunctionEditorProps): JSX.Element {
  const {classes} = useStyles();
  const {pipelineStepId} = useStepConfigEditorContext();
  const value = (fullConfig[fieldName] as string | null) ?? DEFAULT_VALUE[mode];

  const handleChange = useCallback(
    (newValue: string): void => {
      onChangeConfig(fullConfig, [fieldName], newValue);
    },
    [onChangeConfig, fullConfig, fieldName]
  );

  // The Custom Code Writer targets a specific pipeline step, so only offer it when the step id is
  // available (it is threaded through the step-config editor context).
  const canGenerateWithAi =
    BenchlingConfig.LLM_ENABLE_PIPELINES_CUSTOM_CODE_WRITER_AGENT && pipelineStepId != null;

  return (
    <div className={classes.editorWrapper}>
      {canGenerateWithAi && (
        <Group position="right">
          <PipelineCustomCodeWriterButton
            pipelineStepId={pipelineStepId}
            disabled={disabled}
            onGeneratedCode={handleChange}
          />
        </Group>
      )}
      <AceEditor
        value={value}
        onChange={handleChange}
        mode={mode}
        readOnly={disabled}
        showGutter={true}
        showPrintMargin={false}
        wrapEnabled={true}
        width="100%"
        height="100%"
      />
    </div>
  );
}
